import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { FEED_MAX_LIVE, feedGapMs, pushFeed } from './pickupTrackingLogic';
import type { TrackingFeedEvent } from './pickupTrackingTypes';

// 与原型 pushEvent 一致：延时 120ms 开始逐字打出，每字 40ms；被挤出的条目播 450ms 退场动画后移除
const TYPE_START_MS = 120;
const TYPE_CHAR_MS = 40;
const OUT_MS = 450;

export interface FeedView {
    id: number;
    driverKey: string;
    driverName: string;
    siteName: string;
    fail: boolean;
    /** 入队时按当时语言定稿，与原型一致（切语言不改已在场的条目） */
    body: string;
    typedLen: number;
    typed: boolean;
    out: boolean;
}

export interface FeedQueueItem {
    e: TrackingFeedEvent;
    body: string;
}

/** 实时 feed：最多 FEED_MAX_LIVE 条在场，新条逐字打出；hubKey 变化时清空（含排队中的） */
export const useTrackingFeed = (
    hubKey: string | null,
): {
    feed: FeedView[];
    push: (e: TrackingFeedEvent, body: string) => void;
    enqueue: (items: FeedQueueItem[], spanMs: number) => void;
} => {
    const [feed, setFeedState] = useState<FeedView[]>([]);
    // 队列以 ref 为准、同步计算后再 setState，避免依赖 updater 的执行时机
    const feedRef = useRef<FeedView[]>([]);
    const timers = useRef<Set<number>>(new Set());
    const queueRef = useRef<FeedQueueItem[]>([]);
    const drainRef = useRef<number | null>(null);

    const setFeed = useCallback((next: FeedView[]) => {
        feedRef.current = next;
        setFeedState(next);
    }, []);

    const later = useCallback((fn: () => void, ms: number): number => {
        const id = window.setTimeout(() => {
            timers.current.delete(id);
            fn();
        }, ms);
        timers.current.add(id);
        return id;
    }, []);

    const push = useCallback(
        (e: TrackingFeedEvent, body: string) => {
            const entry: FeedView = { ...e, body, typedLen: 0, typed: false, out: false };
            const res = pushFeed(feedRef.current, entry, FEED_MAX_LIVE);
            setFeed(res.feed);
            res.evicted.forEach((id) =>
                later(() => setFeed(feedRef.current.filter((f) => f.id !== id)), OUT_MS),
            );

            const type = () => {
                const target = feedRef.current.find((f) => f.id === entry.id);
                // 已被移除（挤出 / 换枢纽清空）就不再打
                if (!target) return;
                const n = target.typedLen + 1;
                const typed = n >= target.body.length;
                setFeed(feedRef.current.map((f) => (f.id === entry.id ? { ...f, typedLen: n, typed } : f)));
                if (!typed) later(type, TYPE_CHAR_MS);
            };
            later(type, TYPE_START_MS);
        },
        [later, setFeed],
    );

    /**
     * 一批事件排队，在 spanMs 内均匀逐条弹出（先弹一条）；上一批没弹完时与新批合并、按剩余条数重算间隔。
     * spanMs <= 0 直接全部弹出（冷启动兜底）
     */
    const enqueue = useCallback(
        (items: FeedQueueItem[], spanMs: number) => {
            if (items.length === 0) return;
            if (spanMs <= 0) {
                items.forEach((it) => push(it.e, it.body));
                return;
            }
            queueRef.current = [...queueRef.current, ...items];
            if (drainRef.current !== null) {
                window.clearTimeout(drainRef.current);
                timers.current.delete(drainRef.current);
                drainRef.current = null;
            }
            const gap = feedGapMs(queueRef.current.length, spanMs);
            const drain = () => {
                const next = queueRef.current.shift();
                drainRef.current = null;
                if (!next) return;
                push(next.e, next.body);
                if (queueRef.current.length > 0) drainRef.current = later(drain, gap);
            };
            drain();
        },
        [later, push],
    );

    // 用 layout effect：同一次提交里数据 hook 的 effect 会推冷启动兜底，清空必须先于它，否则刚推的被清掉
    useLayoutEffect(() => {
        setFeed([]);
        queueRef.current = [];
        drainRef.current = null;
        const set = timers.current;
        return () => {
            set.forEach((id) => window.clearTimeout(id));
            set.clear();
        };
    }, [hubKey, setFeed]);

    return { feed, push, enqueue };
};
